import { FiGitBranch } from 'react-icons/fi';

export const gerundsVsInfinitivesData = {
  courseId: 'gerunds-vs-infinitives',
  courseType: 'grammar',
  title: 'Gerunds vs Infinitives',
  icon: FiGitBranch,
  overview: {
    impact: 'Crucial for grammatical accuracy',
    focus: 'Knowing when to use -ing vs to + verb',
    uses: 'Writing and Speaking',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Verbs That Change Meaning',
      content: `<p>Some verbs can take both gerunds and infinitives, but the meaning changes:</p>`,
      table: {
        headers: ['Verb', 'With Gerund', 'With Infinitive'],
        rows: [
          ['remember', 'I remember meeting her. (past action)', 'Remember to call me. (future action)'],
          ['stop', 'Stop smoking. (quit the habit)', 'Stop to smoke. (pause to smoke)'],
          ['try', 'Try using a dictionary. (experiment)', 'Try to finish early. (make an effort)'],
          ['forget', 'I forgot locking the door. (past action)', 'Don\'t forget to lock the door. (future action)'],
        ],
      },
    },
    {
      title: 'Quick Reference Guide',
      content: `<p>Here's a quick summary of which form to use:</p>`,
      points: [
        'After prepositions: ALWAYS gerund ("good at speaking")',
        'After enjoy, avoid, finish, mind, suggest: ALWAYS gerund',
        'After want, need, decide, plan, hope: ALWAYS infinitive',
        'After like, love, hate, prefer: BOTH are correct',
        'As sentence subject: BOTH work, gerund more common',
      ],
    },
    {
      title: 'Common Errors to Avoid',
      content: `<p>Watch out for these common mistakes:</p>`,
      examples: [
        { label: 'Wrong', text: 'I look forward to see you.' },
        { label: 'Correct', text: 'I look forward to seeing you.' },
        { label: 'Wrong', text: 'She suggested to go early.' },
        { label: 'Correct', text: 'She suggested going early.' },
      ],
      warning: '"Look forward to" always takes a gerund because "to" is a preposition here, not part of an infinitive!',
    },
  ],
  exercises: [
    {
      title: 'Gerund or Infinitive?',
      type: 'select',
      instructions: 'Choose the correct form based on meaning.',
      questions: [
        {
          text: 'I remember ___ the door. (I recall the past action)',
          options: ['locking', 'to lock'],
          answer: 'locking',
          explanation: 'Gerund refers to remembering a past action.',
        },
        {
          text: 'Remember ___ the lights. (Don\'t forget to do this)',
          options: ['to turn off', 'turning off'],
          answer: 'to turn off',
          explanation: 'Infinitive refers to a future action to remember.',
        },
        {
          text: 'I look forward to ___ you soon.',
          options: ['meeting', 'meet'],
          answer: 'meeting',
          explanation: '"Look forward to" uses a gerund (preposition "to").',
        },
        {
          text: 'Try ___ the back door. (Experiment with this)',
          options: ['using', 'to use'],
          answer: 'using',
          explanation: 'Gerund suggests experimenting with an approach.',
        },
        {
          text: 'She stopped ___ at the shop. (She paused to do this)',
          options: ['to look', 'looking'],
          answer: 'to look',
          explanation: 'Stop + infinitive = pause to do something.',
        },
        {
          text: 'He stopped ___. (He quit the habit)',
          options: ['smoking', 'to smoke'],
          answer: 'smoking',
          explanation: 'Stop + gerund = quit doing something.',
        },
      ],
    },
    {
      title: 'Meaning Difference',
      type: 'select',
      instructions: 'What\'s the difference in meaning?',
      questions: [
        {
          text: '"I forgot calling her" vs "I forgot to call her"',
          options: [
            'Forgot I had called vs Forgot I should call',
            'Same meaning',
            'Both past actions',
          ],
          answer: 'Forgot I had called vs Forgot I should call',
          explanation: 'Gerund = forgot a past action. Infinitive = forgot a future action.',
        },
        {
          text: '"She stopped to smoke" vs "She stopped smoking"',
          options: [
            'Paused to have a cigarette vs Quit the habit',
            'Same meaning',
            'Both mean quit',
          ],
          answer: 'Paused to have a cigarette vs Quit the habit',
          explanation: 'Stop + infinitive = pause. Stop + gerund = quit.',
        },
        {
          text: '"I tried eating less" vs "I tried to eat less"',
          options: [
            'Experimented with eating less vs Made an effort to eat less',
            'Same meaning',
            'Experimented with reducing food',
          ],
          answer: 'Experimented with eating less vs Made an effort to eat less',
          explanation: 'Gerund = experiment. Infinitive = attempt.',
        },
      ],
    },
    {
      title: 'Complete with Correct Form',
      type: 'fill-in',
      instructions: 'Write the correct form (gerund or infinitive).',
      questions: [
        {
          text: 'I\'m interested in ___ (learn) about history.',
          answer: 'learning',
          explanation: 'After "interested in" (preposition), use gerund.',
        },
        {
          text: 'She decided ___ (study) abroad.',
          answer: 'to study',
          explanation: '"Decide" is always followed by infinitive.',
        },
        {
          text: 'Would you mind ___ (close) the window?',
          answer: 'closing',
          explanation: '"Mind" is always followed by gerund.',
        },
        {
          text: 'I hope ___ (see) you again soon.',
          answer: 'to see',
          explanation: '"Hope" is always followed by infinitive.',
        },
      ],
    },
    {
      title: 'Fix the Errors',
      type: 'writing',
      instructions: 'Correct the gerund/infinitive errors.',
      questions: [
        {
          text: '"I look forward to see you next week."',
          example: 'I look forward to seeing you next week.',
          tip: '"Look forward to" takes a gerund because "to" is a preposition.',
        },
        {
          text: '"She suggested to go to the beach."',
          example: 'She suggested going to the beach.',
          tip: '"Suggest" takes a gerund.',
        },
        {
          text: '"I\'m used to work late."',
          example: 'I\'m used to working late.',
          tip: '"Used to" (accustomed) takes a gerund.',
        },
      ],
    },
    {
      title: 'Quick Reference Check',
      type: 'select',
      instructions: 'Which form does this verb take?',
      questions: [
        {
          text: 'enjoy',
          options: ['Always gerund', 'Always infinitive', 'Both'],
          answer: 'Always gerund',
          explanation: '"Enjoy" is always followed by a gerund.',
        },
        {
          text: 'want',
          options: ['Always infinitive', 'Always gerund', 'Both'],
          answer: 'Always infinitive',
          explanation: '"Want" is always followed by an infinitive.',
        },
        {
          text: 'like',
          options: ['Both', 'Always gerund', 'Always infinitive'],
          answer: 'Both',
          explanation: '"Like" can take either gerund or infinitive.',
        },
      ],
    },
  ],
  tips: [
    'Memorize verbs with meaning changes (remember, stop, try)',
    'After prepositions, always use gerunds',
    'When in doubt with like/love/hate, both are acceptable',
  ],
  dailyPractice: [
    'Practice sentences with remember/forget + gerund/infinitive',
    'Identify gerunds and infinitives in reading texts',
    'Write sentences showing the meaning difference',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Gerunds vs Infinitives: Understanding when to use -ing forms or to + verb, including verbs that change meaning and common patterns.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...gerundsVsInfinitivesData,
      title: gerundsVsInfinitivesData.title + ' (Foundations)',
      overview: {
        ...gerundsVsInfinitivesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: gerundsVsInfinitivesData.sections.slice(0, Math.max(2, Math.ceil(gerundsVsInfinitivesData.sections.length / 2))),
      exercises: gerundsVsInfinitivesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...gerundsVsInfinitivesData,
      title: gerundsVsInfinitivesData.title + ' (Advanced)',
      overview: {
        ...gerundsVsInfinitivesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: gerundsVsInfinitivesData.sections,
      exercises: gerundsVsInfinitivesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(gerundsVsInfinitivesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return gerundsVsInfinitivesData;
}
